import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {
  ContextRegionPayload,
  LocalNavigationActions,
  LocalNavigationTab,
  NavigationTabCountUpdate,
  ReactNavTarget,
  TurboNavAttrs,
} from '@github-ui/global-navigation-types'
import {usePreheatIssuesIndex} from '@github-ui/preheat-issues-manager/use-preheat-issues-index'
import {Link} from '@github-ui/react-router'
import {isUIService} from '@github-ui/runtime-environment'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useAlive} from '@github-ui/use-alive'
import {useMultiRef} from '@github-ui/use-multi-ref'
import {useSyncedState} from '@github-ui/use-synced-state'
import {Tooltip, UnderlineNav} from '@primer/react'
import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import {getCurrentSelectedLink} from '../context-region/context-region-navigation-state'
import {
  formatNavigationTabCount,
  markNavigationTabCountUpdated,
  registerLocalNavigationHandler,
} from '../global-navigation'
import {useGlobalNavAnalytics} from '../utils/Analytics'
import {isNavOcticonName, NavOcticon} from '../utils/octicons'
import {LazyNavigationTabPopover} from './LazyNavigationTabPopover'
import styles from './LocalNavigation.module.css'

/** Extracts react-specific navigation attributes from the context region payload */
function getReactNavAttrs(target: ReactNavTarget | undefined): Record<string, string | undefined> {
  if (!target) {
    return {}
  }

  return {
    'data-react-nav': target.appTarget,
    ...(target.anchor ? {'data-react-nav-anchor': target.anchor} : {}),
  }
}

type PrefetchEagerness = 'immediate' | 'eager' | 'moderate' | 'conservative'

function getPrefetchAttrs(tabId: string, eagerness: PrefetchEagerness): Record<string, string | undefined> {
  if (tabId === 'code' || !isFeatureEnabled('speculation_rules_ui_service') || !isUIService) return {}

  return {
    'data-prefetch': eagerness,
  }
}

function getTurboNavAttrs(target: TurboNavAttrs | undefined): Record<string, string | undefined> {
  if (!target) {
    return {}
  }

  return {
    'data-turbo-frame': target.frame,
  }
}

function findActiveTabBySelectedLink(
  tabs?: LocalNavigationTab[],
  selectedLink?: string,
  currentPath?: string,
): LocalNavigationTab | undefined {
  if (!tabs || tabs.length === 0) {
    return undefined
  }

  const path = currentPath || ssrSafeLocation.pathname + ssrSafeLocation.search

  // First, try to find a tab matching the selectedLink prop or document metadata.
  const currentSelectedLink = getCurrentSelectedLink(selectedLink)
  if (currentSelectedLink) {
    const selectedLinkTab = tabs.find(tab => tab.selectedLinks?.includes(currentSelectedLink))
    if (selectedLinkTab) {
      return selectedLinkTab
    }
  }

  // Last resort: infer the active tab based on the current path
  const currentPathLower = path.toLowerCase()
  const urlMatchedTabs = tabs.filter(tab => {
    const hrefLower = tab.href?.toLowerCase()
    if (!hrefLower) return false
    // Strip query params from tab href for comparison so hrefs like
    // "/repo/agents?author=me" still match path "/repo/agents"
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const hrefPath = hrefLower.split('?')[0]!
    return (
      currentPathLower === hrefPath ||
      currentPathLower.startsWith(`${hrefPath}/`) ||
      currentPathLower.startsWith(`${hrefPath}?`)
    )
  })

  // Return the tab with the longest matching path (most specific match).
  // Compare on the path portion only so a query string doesn't inflate specificity.
  const pathLength = (tab: LocalNavigationTab) => tab.href?.split('?')[0]?.length || 0
  return urlMatchedTabs.sort((a, b) => pathLength(b) - pathLength(a))[0]
}

/**
 * Hook to track which tab is currently active based on the selected link or the current URL
 * Listens for turbo navigation events and updates the active tab accordingly
 */
function useActiveTab(tabs?: LocalNavigationTab[], defaultSelectedLink?: string, currentPath?: string) {
  const [activeTab, setActiveTab] = useState<LocalNavigationTab | undefined>(() => {
    return findActiveTabBySelectedLink(tabs || [], defaultSelectedLink, currentPath)
  })

  // A selection pushed imperatively (repo-app soft nav) or by the <meta> observer isn't reflected in
  // the server-provided props, so it must survive a `tabs`-only recomputation (e.g. a tab-count or
  // Alive update that replaces the array). We keep the pushed identifier and re-resolve from it below,
  // clearing it whenever a real prop/Turbo selection supersedes it.
  const [pushedSelectedLink, setPushedSelectedLink] = useState<string | undefined>(undefined)

  // Recompute the active tab during render when its inputs change (e.g. client-side navigation
  // updates selectedLink). Setting state during render cancels the in-progress render and
  // re-renders immediately, avoiding the extra commit + post-paint flash that a set-state-in-effect
  // would cause on every prop change.
  const [prevInputs, setPrevInputs] = useState({tabs, defaultSelectedLink, currentPath})
  if (
    prevInputs.tabs !== tabs ||
    prevInputs.defaultSelectedLink !== defaultSelectedLink ||
    prevInputs.currentPath !== currentPath
  ) {
    // A changed prop selection (server/Turbo) is authoritative and supersedes an imperative push; a
    // `tabs`-only change instead re-resolves from the still-active pushed identifier when there is one.
    const propSelectionChanged =
      prevInputs.defaultSelectedLink !== defaultSelectedLink || prevInputs.currentPath !== currentPath
    const effectiveSelectedLink =
      !propSelectionChanged && pushedSelectedLink !== undefined ? pushedSelectedLink : defaultSelectedLink

    // Forget the push once a real prop/Turbo selection wins, so a later `tabs`-only recompute can't
    // resurrect the stale imperative selection.
    if (propSelectionChanged && pushedSelectedLink !== undefined) {
      setPushedSelectedLink(undefined)
    }
    setPrevInputs({tabs, defaultSelectedLink, currentPath})
    setActiveTab(findActiveTabBySelectedLink(tabs || [], effectiveSelectedLink, currentPath))
  }

  // Keep the latest tabs in a ref so the imperative setter stays referentially stable (the
  // handler is registered once) while still resolving against the current tabs.
  const tabsRef = useRef(tabs)
  useEffect(() => {
    tabsRef.current = tabs
  }, [tabs])

  // Resolve `selectedLink` to a tab, record the identifier (so a later `tabs`-only recompute
  // re-resolves from it instead of the stale server prop), and activate it. Shared by imperative
  // repo-app soft navs — which fire no turbo:load and never rewrite the <meta name="selected-link">,
  // so the tab must be set directly — and by the <meta> observer below (which may pass `undefined` to
  // fall back to meta/URL resolution).
  const setActiveBySelectedLink = useCallback((selectedLink: string | undefined) => {
    const tab = findActiveTabBySelectedLink(tabsRef.current || [], selectedLink)
    if (tab) {
      setPushedSelectedLink(selectedLink)
      setActiveTab(tab)
    }
  }, [])

  // Matches the behavior from the Rails listeners in app/assets/modules/github/behaviors/side-navigation.ts
  useEffect(() => {
    function updateActiveTabFromMetaElement(node: HTMLMetaElement) {
      if (node.isConnected && node.getAttribute('name') === 'selected-link') {
        // Reuse the imperative setter so the meta/Turbo selection is recorded the same way and
        // survives a later `tabs`-only recompute.
        setActiveBySelectedLink(node.getAttribute('value') || undefined)
      }
    }

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLMetaElement) {
            updateActiveTabFromMetaElement(node)
          }
        }
      }
    })

    if (document?.head) {
      observer.observe(document.head, {childList: true})
    }

    const turboEventHandler = () => {
      const selectedLinkElement = document.head?.querySelector<HTMLMetaElement>('meta[name="selected-link"]')
      if (selectedLinkElement) {
        updateActiveTabFromMetaElement(selectedLinkElement)
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('turbo:load', turboEventHandler)
    }

    return () => {
      observer.disconnect()
      window?.removeEventListener('turbo:load', turboEventHandler)
    }
  }, [setActiveBySelectedLink])

  return {activeTab, setActiveBySelectedLink}
}

const crumbToLabelMap: Record<string, string> = {
  repository: 'Repository',
  business: 'Enterprise',
  organization: 'Organization',
  user: 'User',
}

function getAriaLabel(crumbs: ContextRegionPayload['crumbs']) {
  const lastCrumb = crumbs?.[crumbs.length - 1]
  if (lastCrumb && lastCrumb.crumb_type) {
    return crumbToLabelMap[lastCrumb.crumb_type] ?? 'Local'
  }
  return 'Local'
}

/** Convert tab ID to analytics action format (e.g., 'pull-requests' -> 'PULL_REQUESTS') */
function getTabAnalyticsAction(tabId: string): string {
  return tabId.toUpperCase().replace(/-/g, '_')
}

/** Renders the local tabs and other nav items specific to this particular route */
export function LocalNavigation({
  localNavigation: defaultLocalNavigation,
  localNavigationUpdateChannel,
  selectedLink,
  crumbs,
  currentPath,
}: ContextRegionPayload) {
  // Sync the tabs to the `localNavigation` prop during render rather than in a post-paint effect.
  // The internal setter is still used for live (Alive) updates and imperative tab-count changes;
  // when the prop reference changes, the state resets to match it — matching the previous
  // effect-based behavior, but without the extra commit + flash.
  const [localNavigation, setLocalNavigation] = useSyncedState<LocalNavigationTab[] | undefined>(defaultLocalNavigation)
  const localNavigationRef = useRef(localNavigation)
  const exactClientCountsRef = useRef(new Set<string>())
  const syncedDefaultNavigationRef = useRef(defaultLocalNavigation)

  useLayoutEffect(() => {
    if (syncedDefaultNavigationRef.current === defaultLocalNavigation) return
    syncedDefaultNavigationRef.current = defaultLocalNavigation
    const previousPullRequestsTab = localNavigationRef.current?.find(tab => tab.id === 'pull-requests')
    const nextPullRequestsTab = defaultLocalNavigation?.find(tab => tab.id === 'pull-requests')
    if (previousPullRequestsTab || nextPullRequestsTab) {
      markNavigationTabCountUpdated('pull-requests', previousPullRequestsTab === undefined)
    }
    localNavigationRef.current = defaultLocalNavigation
    exactClientCountsRef.current.clear()
  }, [defaultLocalNavigation])

  const {activeTab, setActiveBySelectedLink} = useActiveTab(localNavigation, selectedLink, currentPath)
  const {sendItemClick} = useGlobalNavAnalytics('LOCAL_NAV')
  const crumbType = crumbs?.[crumbs.length - 1]?.crumb_type

  const repoCrumb = crumbs?.find(c => c.crumb_type === 'repository')
  const ownerCrumb = repoCrumb ? crumbs?.[crumbs.indexOf(repoCrumb) - 1] : undefined
  usePreheatIssuesIndex(ownerCrumb?.label, repoCrumb?.label, activeTab?.id)

  useAlive<{tabs: LocalNavigationTab[]}>(localNavigationUpdateChannel, event => {
    if (event.tabs) {
      const previousPullRequestsTab = localNavigationRef.current?.find(tab => tab.id === 'pull-requests')
      const nextPullRequestsTab = event.tabs.find(tab => tab.id === 'pull-requests')
      if (previousPullRequestsTab || nextPullRequestsTab) {
        markNavigationTabCountUpdated('pull-requests', previousPullRequestsTab === undefined)
      }
      exactClientCountsRef.current.clear()
      localNavigationRef.current = event.tabs
      setLocalNavigation(event.tabs)
    }
  })

  const updateTabCount = useCallback(
    (tabId: string, update: NavigationTabCountUpdate) => {
      const currentNavigation = localNavigationRef.current
      const currentTab = currentNavigation?.find(tab => tab.id === tabId)
      if (!currentNavigation || !currentTab) return undefined

      const currentCount = currentTab.count ?? 0
      let nextCount: number
      if (update.type === 'delta') {
        if (tabId === 'pull-requests' && currentCount > 5000 && !exactClientCountsRef.current.has(tabId)) return false
        exactClientCountsRef.current.add(tabId)
        nextCount = currentCount + update.delta
      } else if (update.type === 'countIfCurrent') {
        if (tabId === 'pull-requests' && currentCount > 5000 && !exactClientCountsRef.current.has(tabId)) return false
        if (!update.expectedCounts.includes(currentCount)) return false
        exactClientCountsRef.current.add(tabId)
        nextCount = update.count
      } else {
        if (update.exact) {
          exactClientCountsRef.current.add(tabId)
        } else {
          exactClientCountsRef.current.delete(tabId)
        }
        nextCount = update.count
      }

      const normalizedNextCount = Math.max(nextCount, 0)
      const nextNavigation = currentNavigation.map(tab =>
        tab.id === tabId ? {...tab, count: normalizedNextCount} : tab,
      )
      localNavigationRef.current = nextNavigation
      setLocalNavigation(nextNavigation)
      return true
    },
    [setLocalNavigation],
  )
  const getTabCount = useCallback((tabId: string) => {
    const currentTab = localNavigationRef.current?.find(tab => tab.id === tabId)
    if (!currentTab) return undefined
    return {
      count: currentTab.count ?? 0,
      exact: tabId !== 'pull-requests' || (currentTab.count ?? 0) <= 5000 || exactClientCountsRef.current.has(tabId),
    }
  }, [])

  const ref = useRef<LocalNavigationActions | null>(null)

  useImperativeHandle(
    ref,
    () => ({
      getTabCount,
      updateTabCount,
      setActiveTab: setActiveBySelectedLink,
    }),
    [getTabCount, setActiveBySelectedLink, updateTabCount],
  )

  const hasPullRequestsTab = localNavigation?.some(tab => tab.id === 'pull-requests') ?? false
  useEffect(() => {
    const {current} = ref
    return current ? registerLocalNavigationHandler(current) : undefined
  }, [hasPullRequestsTab])

  // The popoover will be entirely suppressed in older browsers that don't support anchor positioning
  const {isBrowser} = useRuntimeEnvironment()
  const supportsPopoverAnchorPosition = useMemo(() => {
    if (!isBrowser) return false
    try {
      return Boolean(CSS.supports('position-anchor', '--name'))
    } catch {
      return false
    }
  }, [isBrowser])
  const [popoverDismissed, dismissPopover] = useReducer(() => true, false)
  const popoverId = useId()

  const itemsRef = useMultiRef<string, HTMLAnchorElement>()
  // UI Service navigation is handled by TanStack Router and has no <turbo-frame>. On dotcom, Code
  // also drops its Turbo target when UI Service routing is enabled to avoid a wasted double load.
  const codeViewEnabled = isFeatureEnabled('ui_service_eligible') && isFeatureEnabled('ui_route_code_view')

  if (!localNavigation || localNavigation.length === 0) {
    return null
  }

  const underlineNavKey = localNavigation.map(tab => tab.id).join('-')

  // Only one popover tab is permitted, so we find the first one and use that
  const popoverTabId =
    supportsPopoverAnchorPosition && !popoverDismissed
      ? localNavigation.find(tab => tab.popoverData !== undefined)?.id
      : undefined

  return (
    <>
      <UnderlineNav key={underlineNavKey} aria-label={getAriaLabel(crumbs)} className={styles.LocalNavigation}>
        {localNavigation.map(tab => {
          const href = tab.href || '#'
          const isCurrentPage = activeTab?.id === tab.id
          const handleTabClick = () =>
            sendItemClick(getTabAnalyticsAction(tab.id), crumbType ? `TYPE:${crumbType.toUpperCase()}` : undefined)
          const turboNav = isUIService || (codeViewEnabled && tab.id === 'code') ? undefined : tab.turboNav

          const commonAttrs = {
            // Resolve the name up front: an unrecognized icon has to leave `leadingVisual`
            // undefined so Primer omits its icon wrapper, rather than keeping the wrapper's
            // end margin around an icon that never renders.
            leadingVisual: tab.icon && isNavOcticonName(tab.icon) ? <NavOcticon name={tab.icon} /> : undefined,
            'aria-current': isCurrentPage ? ('page' as const) : undefined,
            'data-tab-item': tab.id,
            counter: formatNavigationTabCount(tab.count),
            ...getReactNavAttrs(tab.reactNav),
            ...getTurboNavAttrs(turboNav),
          }
          const prefetchAttrs = getPrefetchAttrs(tab.id, 'moderate')

          return tab.id === popoverTabId ? (
            <UnderlineNav.Item as="span" key={tab.id} className={styles.PopoverTab} {...commonAttrs}>
              <Link
                to={href}
                className={styles.PopoverLink}
                {...prefetchAttrs}
                aria-details={popoverId}
                ref={itemsRef(tab.id)}
                onClick={handleTabClick}
              >
                {tab.label}
              </Link>
              {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
              <LazyNavigationTabPopover id={popoverId} popoverData={tab.popoverData!} onDismiss={dismissPopover} />
            </UnderlineNav.Item>
          ) : tab.tooltip ? (
            // A description tooltip keeps the label as the accessible name and adds supplementary
            // text (e.g. why the tab is restricted) via aria-describedby on the tab link.
            <Tooltip key={tab.id} text={tab.tooltip} type="description" direction="s">
              <UnderlineNav.Item
                as={Link}
                to={href}
                {...commonAttrs}
                {...prefetchAttrs}
                ref={itemsRef(tab.id)}
                onClick={handleTabClick}
              >
                {tab.label}
              </UnderlineNav.Item>
            </Tooltip>
          ) : (
            // When no popover is present, prefer rendering a normal tab to increase the click target to the full tab
            <UnderlineNav.Item
              key={tab.id}
              as={Link}
              to={href}
              {...commonAttrs}
              {...prefetchAttrs}
              ref={itemsRef(tab.id)}
              onClick={handleTabClick}
            >
              {tab.label}
            </UnderlineNav.Item>
          )
        })}
      </UnderlineNav>
      <GlobalCommands
        commands={Object.fromEntries(
          localNavigation
            .filter(tab => tab.commandId !== undefined)
            .map(tab => [tab.commandId, () => itemsRef(tab.id).current?.click()]),
        )}
      />
    </>
  )
}
